// GDPR/DSGVO cookie-consent state — persisted in localStorage for 1 year.
// Three categories: `necessary` (always on), `analytics`, `marketing`.
//
// Public API:
//   const { consent, isDecided, showBanner, accept, decline, save, reset } = useConsent();
//   consent.analytics === true  → OK to fire teaser tracking, GA, etc.
//   consent.marketing === true  → OK to fire ads pixels
//
// The saved shape is { version, decided_at_iso, necessary, analytics, marketing }.
// If the schema version changes, the banner reopens automatically.

import { useCallback, useEffect, useState } from "react";

const LS_KEY = "talkani_cookie_consent_v1";
const SCHEMA_VERSION = 1;

const DEFAULT_CONSENT = {
  version: SCHEMA_VERSION,
  necessary: true,   // always true — cannot be disabled
  analytics: false,
  marketing: false,
};

function readStored() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.version !== SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(next) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch { /* private mode */ }
}

export default function useConsent() {
  const [consent, setConsent] = useState(() => readStored() || DEFAULT_CONSENT);
  const [isDecided, setIsDecided] = useState(() => !!readStored());

  // Cross-tab sync — if the user changes consent in another tab, reflect it here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== LS_KEY) return;
      const next = readStored();
      if (next) { setConsent(next); setIsDecided(true); }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const commit = useCallback((patch) => {
    const next = {
      ...DEFAULT_CONSENT,
      ...patch,
      necessary: true,
      version: SCHEMA_VERSION,
      decided_at_iso: new Date().toISOString(),
    };
    writeStored(next);
    setConsent(next);
    setIsDecided(true);
  }, []);

  const acceptAll = useCallback(() => commit({ analytics: true, marketing: true }), [commit]);
  const declineAll = useCallback(() => commit({ analytics: false, marketing: false }), [commit]);
  const save = useCallback((partial) => commit(partial), [commit]);
  const reset = useCallback(() => {
    try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
    setConsent(DEFAULT_CONSENT);
    setIsDecided(false);
  }, []);

  return {
    consent,
    isDecided,
    showBanner: !isDecided,
    acceptAll,
    declineAll,
    save,
    reset,
  };
}
